import { formatPhoneNumber } from '@supply-mover-2-0/core';
import {
	PORTAL_SMS_DISCLOSURE,
	type PortalSupportNotifyPreferencesDto,
	type UpdatePortalSupportNotifyPreferencesDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	canOfferBrowserPush,
	enablePortalBrowserPush,
	isPortalBrowserPushOn,
} from '../lib/portalPush';
import {
	getPortalSupportNotifyPreferences,
	updatePortalSupportNotifyPreferences,
} from '../services/portalNotificationService';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { PORTAL_NOTIFICATION_SETTINGS_PATH } from './PortalNotificationBell';

/**
 * The channels the control reports: only the ones it showed, so saving never
 * changes a channel the customer could not see (e.g. browser push set up on
 * another device, hidden inside the app).
 */
export type SupportNotifyValueModel = UpdatePortalSupportNotifyPreferencesDto;

export interface SupportNotifyControlProps {
	/**
	 * `immediate` saves each change as it is made (the support request page).
	 * `deferred` only reports the value through `onChange`; the support form
	 * saves it with `saveSupportNotifyPreferences` after a successful submit,
	 * which is when text message consent is recorded.
	 */
	mode: 'immediate' | 'deferred';
	onChange?: (value: SupportNotifyValueModel) => void;
	className?: string;
}

/**
 * Save the control's value (both support events). For the support form:
 * call after the request was submitted successfully. Never throws — a
 * notification choice that fails to save must not turn a successful submit
 * into an error — and returns null on failure.
 */
export async function saveSupportNotifyPreferences(
	value: SupportNotifyValueModel,
): Promise<PortalSupportNotifyPreferencesDto | null> {
	if (value.email == null && value.sms == null && value.push == null) {
		return null;
	}
	try {
		return await updatePortalSupportNotifyPreferences(
			usePortalAuthStore.getState().token,
			value,
		);
	} catch (error) {
		console.debug('[SupportNotifyControl] Could not save the choice', error);
		return null;
	}
}

interface CheckedModel {
	email: boolean;
	sms: boolean;
	push: boolean;
}

/**
 * PD-16475 — "Notify me about replies": Email, Text and Browser checkboxes
 * for the customer's support requests, showing only what the account can
 * use (an email address, a phone number, a browser that supports push and
 * is not the storefront app). The Text box carries the consent disclosure,
 * and a customer who replied STOP is told how to turn texts back on, since
 * the app cannot undo a carrier STOP.
 */
export function SupportNotifyControl({
	mode,
	onChange,
	className,
}: SupportNotifyControlProps): JSX.Element | null {
	const { t } = useTranslation();
	const token = usePortalAuthStore((s) => s.token);
	const [prefs, setPrefs] = useState<PortalSupportNotifyPreferencesDto | null>(
		null,
	);
	const [checked, setChecked] = useState<CheckedModel>({
		email: false,
		sms: false,
		push: false,
	});
	const [saving, setSaving] = useState(false);
	const [pushMessage, setPushMessage] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const offerPush = canOfferBrowserPush();

	const report = useCallback(
		(next: CheckedModel, loaded: PortalSupportNotifyPreferencesDto): void => {
			if (onChange == null) {
				return;
			}
			const value: SupportNotifyValueModel = {};
			if (loaded.available.email) {
				value.email = next.email;
			}
			if (loaded.available.sms) {
				value.sms = next.sms;
			}
			if (offerPush) {
				value.push = next.push;
			}
			onChange(value);
		},
		[onChange, offerPush],
	);

	useEffect(() => {
		if (token == null) {
			return;
		}
		// An object, not a `let`, so the async body reads the live flag.
		const run = { cancelled: false };
		void (async (): Promise<void> => {
			try {
				const [loaded, browserOn] = await Promise.all([
					getPortalSupportNotifyPreferences(token),
					isPortalBrowserPushOn(),
				]);
				if (run.cancelled) {
					return;
				}
				const smsOk = !loaded.smsStatus.optedOut;
				const initial: CheckedModel = {
					email: loaded.available.email && loaded.email,
					// The support form pre-checks Text (submitting it is the
					// consent); elsewhere it reads as checked only once texts
					// will actually be sent.
					sms:
						loaded.available.sms &&
						loaded.sms &&
						smsOk &&
						(mode === 'deferred' || loaded.smsStatus.consented),
					push: offerPush && browserOn && loaded.push,
				};
				setPrefs(loaded);
				setChecked(initial);
				report(initial, loaded);
			} catch {
				// Not signed in, or the API is unreachable: show nothing rather
				// than a control that cannot save.
				if (!run.cancelled) {
					setPrefs(null);
				}
			}
		})();
		return (): void => {
			run.cancelled = true;
		};
		// `report` is deliberately left out: reporting the initial value once
		// per load is enough, and a new onChange identity must not refetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, mode, offerPush]);

	const apply = async (
		channel: keyof CheckedModel,
		on: boolean,
	): Promise<void> => {
		if (prefs == null) {
			return;
		}
		setSaveError(null);
		setPushMessage(null);

		if (channel === 'push' && on) {
			// Subscribing asks the browser for permission, so it runs on the
			// click itself, in both modes.
			const enabled = await enablePortalBrowserPush().catch(() => false);
			if (!enabled) {
				setPushMessage(
					typeof Notification !== 'undefined' &&
						Notification.permission === 'denied'
						? 'Your browser is blocking notifications for this site. Allow them in your browser settings, then try again.'
						: 'Browser notifications are not available right now.',
				);
				return;
			}
		}

		const next = { ...checked, [channel]: on };
		setChecked(next);
		report(next, prefs);

		if (mode === 'deferred') {
			return;
		}
		setSaving(true);
		try {
			const saved = await updatePortalSupportNotifyPreferences(token, {
				[channel]: on,
			});
			setPrefs(saved);
		} catch {
			setChecked(checked);
			setSaveError('Your choice could not be saved. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	if (prefs == null) {
		return null;
	}

	const { available, smsStatus } = prefs;
	const showPush = offerPush;
	const hasChoices = available.email || available.sms || showPush;
	const phoneLabel =
		smsStatus.phone != null ? formatPhoneNumber(smsStatus.phone) : '';

	return (
		<fieldset className={className}>
			<legend className="text-sm font-medium text-default">
				{t('Notify me about replies')}
			</legend>

			{prefs.paused && (
				<p className="mt-1 text-xs text-brand-muted">
					{t('Your notifications are paused.')}{' '}
					<Link
						to={PORTAL_NOTIFICATION_SETTINGS_PATH}
						className="text-portal-accent hover:underline"
					>
						{t('Notification settings')}
					</Link>
				</p>
			)}

			{!hasChoices ? (
				<p className="mt-1 text-xs text-brand-muted">
					{t('Replies will appear in your notifications.')}
				</p>
			) : (
				<div className="mt-2 space-y-2">
					{available.email && (
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="checkbox"
								checked={checked.email}
								disabled={saving}
								onChange={(e) => {
									void apply('email', e.target.checked);
								}}
								className="w-4 h-4 text-portal-accent"
							/>
							<span className="text-sm text-default">
								{t('Email')}{' '}
								<span className="text-brand-muted">
									({t(prefs.emailAddress ?? '')})
								</span>
							</span>
						</label>
					)}

					{available.sms && (
						<div>
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={checked.sms}
									disabled={saving}
									onChange={(e) => {
										void apply('sms', e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">
									{t('Text')}{' '}
									<span className="text-brand-muted">({t(phoneLabel)})</span>
								</span>
							</label>
							<p className="ml-6 mt-1 text-xs text-brand-muted">
								{t(PORTAL_SMS_DISCLOSURE)}
							</p>
							{checked.sms && smsStatus.optedOut && (
								<p className="ml-6 mt-1 text-xs text-warning">
									{smsStatus.startNumber != null
										? t(
												'You replied STOP earlier. Text START to {number} to turn texts back on.',
											).replace(
												'{number}',
												formatPhoneNumber(smsStatus.startNumber),
											)
										: t(
												'You replied STOP earlier. Text START to our number to turn texts back on.',
											)}
								</p>
							)}
						</div>
					)}

					{showPush && (
						<div>
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={checked.push}
									disabled={saving}
									onChange={(e) => {
										void apply('push', e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">{t('Browser')}</span>
							</label>
							{pushMessage != null && (
								<p className="ml-6 mt-1 text-xs text-brand-muted">
									{t(pushMessage)}
								</p>
							)}
						</div>
					)}
				</div>
			)}

			{saveError != null && (
				<p className="mt-2 text-xs text-danger">{t(saveError)}</p>
			)}
		</fieldset>
	);
}
